import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Heart, Package, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { formatPrice, formatDate } from '../lib/format';

const AccountPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/customer/orders');
        if (!cancelled && res.data?.success) setOrders(res.data.data || []);
      } catch {/* swallow */}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="bg-white border-4 border-black p-10 max-w-md text-center shadow-[8px_8px_0px_black]">
          <User size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-3xl font-black mb-2">SIGN IN REQUIRED</h2>
          <p className="text-gray-600 mb-6">Login to see your orders & wishlist.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-yellow-400 text-black px-6 py-3 font-black border-4 border-black"
          >
            ← Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="text-xs font-mono uppercase opacity-60">Logged in as</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">{user?.name || user?.email}</h1>
          <div className="text-sm font-mono opacity-70">{user?.email}</div>
        </div>
        <button
          onClick={logout}
          className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-3 font-bold uppercase tracking-widest border-2 border-black"
        >
          <LogOut size={18} /> Logout
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <Link to="/account/wishlist" className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_black] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all">
          <Heart size={32} className="text-red-500 mb-3" />
          <div className="font-black uppercase">Wishlist</div>
          <div className="text-xs font-mono opacity-60">Items you love</div>
        </Link>
        <Link to="/order/track" className="bg-white border-4 border-black p-6 shadow-[4px_4px_0px_black] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all">
          <Package size={32} className="text-yellow-500 mb-3" />
          <div className="font-black uppercase">Track Order</div>
          <div className="text-xs font-mono opacity-60">By order # + phone</div>
        </Link>
        <Link to="/anime" className="bg-black text-white p-6 shadow-[4px_4px_0px_black] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all">
          <div className="font-black uppercase text-xl">Shop new</div>
          <div className="text-xs font-mono opacity-60">→ Latest drops</div>
        </Link>
      </div>

      <h2 className="text-2xl font-black mb-4">YOUR ORDERS</h2>
      {loading ? (
        <div className="font-bold animate-pulse">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="bg-white border-4 border-black p-8 text-center shadow-[4px_4px_0px_black]">
          <p className="font-bold">No orders yet.</p>
          <Link to="/anime" className="inline-block mt-3 underline font-bold">Start shopping →</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o._id} className="bg-white border-4 border-black p-5 shadow-[4px_4px_0px_black] flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-mono text-sm">{o.orderNumber}</div>
                <div className="text-xs font-bold uppercase opacity-60">{formatDate(o.createdAt)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-bold">{formatPrice(o.pricing?.total ?? 0)}</span>
                <span className="text-xs font-black uppercase bg-yellow-400 px-2 py-1 border-2 border-black">
                  {o.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountPage;
